import React, { Suspense, lazy, useState, useEffect } from 'react';
import ErrorBoundary from '../components/Charts/ErrorBoundary';
import {
  Activity, Shield, Zap, Globe, Bell, Terminal, Cpu, Network,
  Eye, Crosshair, AlertOctagon, Radio, Wifi,
  Clock, Lock, HardDrive, Loader2,
} from 'lucide-react';
import { apiClient } from '../api';

const TrafficChart = lazy(() => import('../components/Charts/TrafficChart'));
const SeverityDonut = lazy(() => import('../components/Charts/SeverityDonut'));

const Dashboard = () => {
  const [stats, setStats] = useState({
    riskScore: 0,
    assets: 0,
    events: 0,
    health: 'Live',
    isLoading: true,
  });
  const [riskDistribution, setRiskDistribution] = useState({
    CRITICAL: 0, HIGH: 0, MEDIUM: 0, LOW: 0,
  });
  const [recentAlerts, setRecentAlerts] = useState([]);
  const [dataError, setDataError] = useState(null);

  const fetchStats = async () => {
    try {
      const [dashboardRes, riskRes, alertsRes] = await Promise.allSettled([
        apiClient.get('/api/dashboard'),
        apiClient.get('/api/dashboard/risk'),
        apiClient.get('/api/alerts/unified'),
      ]);

      if (dashboardRes.status === 'fulfilled') {
        const { total_servers, active_ioc, active_cve } = dashboardRes.value.data;
        setStats(prev => ({
          ...prev,
          assets: total_servers || 0,
          events: (active_ioc || 0) + (active_cve || 0),
          health: 'Live',
          isLoading: false,
        }));
      } else {
        setStats(prev => ({ ...prev, health: 'Offline', isLoading: false }));
        setDataError('Failed to load dashboard stats');
      }

      if (riskRes.status === 'fulfilled') {
        const servers = riskRes.value.data.servers || [];
        if (servers.length > 0) {
          const avgRisk = Math.round(
            servers.reduce((sum, s) => sum + s.risk_score, 0) / servers.length
          );
          setStats(prev => ({ ...prev, riskScore: avgRisk }));

          const dist = { CRITICAL: 0, HIGH: 0, MEDIUM: 0, LOW: 0 };
          servers.forEach(s => {
            if (s.risk_score > 75) dist.CRITICAL++;
            else if (s.risk_score > 50) dist.HIGH++;
            else if (s.risk_score > 25) dist.MEDIUM++;
            else dist.LOW++;
          });
          setRiskDistribution(dist);
        }
      }

      if (alertsRes.status === 'fulfilled') {
        const alerts = alertsRes.value.data.alerts || [];
        setRecentAlerts(alerts.slice(0, 5));
      }
    } catch (err) {
      setStats(prev => ({ ...prev, isLoading: false, health: 'Offline' }));
      setDataError(err.message);
    }
  };

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, []);

  const getAlertType = (alert) => {
    if (alert.type === 'cve') return 'CVE';
    if (alert.misp_threat_level) return 'Threat Intel';
    return 'System';
  };

  const getAlertSeverity = (alert) => {
    if (alert.type === 'cve') {
      if (alert.severity === 'CRITICAL') return 'high';
      if (alert.severity === 'HIGH') return 'high';
      if (alert.severity === 'MEDIUM') return 'medium';
      return 'low';
    }
    if (alert.misp_threat_level === 'high') return 'high';
    if (alert.misp_threat_level === 'medium') return 'medium';
    return 'low';
  };

  const getAlertMessage = (alert) => {
    if (alert.type === 'cve') {
      return `${alert.package_name} ${alert.version} — ${alert.cve_id}`;
    }
    return alert.description || `${alert.ioc_type}: ${alert.ioc_value}`;
  };

  const getAlertTime = (detectedAt) => {
    if (!detectedAt) return 'Unknown';
    const diff = Date.now() - new Date(detectedAt).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs} hour${hrs > 1 ? 's' : ''} ago`;
    return `${Math.floor(hrs / 24)} day${Math.floor(hrs / 24) > 1 ? 's' : ''} ago`;
  };

  return (
    <div className="min-h-screen bg-gray-950 text-gray-100 p-6 space-y-6">

      {/* ── Top Status Bar ── */}
      <div className="flex items-center justify-between border-b border-gray-800 pb-4">
        <div className="flex items-center gap-3">
          <Shield className="w-7 h-7 text-emerald-400" />
          <div>
            <h1 className="text-xl font-bold tracking-wide text-white">
              UM-DragonEye <span className="text-emerald-400"></span>
            </h1>
            <p className="text-xs text-gray-500 font-mono">Security Operations Center — Operations Overview</p>
          </div>
        </div>
        <div className="flex items-center gap-6 text-xs font-mono">
          <div className="flex items-center gap-2">
            <span className={`inline-block w-2 h-2 rounded-full ${stats.health === 'Live' ? 'bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]' : 'bg-red-500'}`} />
            <span className={stats.health === 'Live' ? 'text-emerald-400' : 'text-red-400'}>{stats.health}</span>
          </div>
          <div className="flex items-center gap-2 text-gray-500">
            <Clock className="w-3.5 h-3.5" />
            <span>{new Date().toLocaleTimeString('en-US', { hour12: false })}</span>
          </div>
          <div className="flex items-center gap-2 text-gray-500">
            <Wifi className="w-3.5 h-3.5" />
            <span>API Connected</span>
          </div>
        </div>
      </div>

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          label="RISK SCORE"
          value={stats.riskScore}
          unit="/100"
          icon={Shield}
          color="text-amber-400"
          glow="shadow-[0_0_12px_rgba(251,191,36,0.3)]"
          trend="-3%"
          trendDir="down"
        />
        <KPICard
          label="TOTAL ASSETS"
          value={stats.assets}
          icon={HardDrive}
          color="text-cyan-400"
          glow="shadow-[0_0_12px_rgba(34,211,238,0.3)]"
          trend="+0"
          trendDir="flat"
        />
        <KPICard
          label="SECURITY EVENTS"
          value={stats.events}
          icon={AlertOctagon}
          color="text-red-400"
          glow="shadow-[0_0_12px_rgba(248,113,113,0.3)]"
          trend="+12"
          trendDir="up"
        />
        <KPICard
          label="SYSTEM HEALTH"
          value={stats.health}
          icon={Zap}
          color="text-emerald-400"
          glow="shadow-[0_0_12px_rgba(52,211,153,0.3)]"
          trend="stable"
          trendDir="flat"
        />
      </div>

      {/* ── Main Grid: Threat Map + Activity ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Threat Map Placeholder */}
        <Panel className="lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-cyan-400" />
              <h2 className="text-sm font-bold tracking-wider text-gray-200 uppercase">Global Threat Map</h2>
            </div>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
              Live
            </span>
          </div>
          <div className="relative h-72 bg-gray-950/60 rounded border border-gray-800 flex items-center justify-center overflow-hidden">
            <div className="absolute inset-0 opacity-[0.06]"
              style={{
                backgroundImage: 'linear-gradient(rgba(34,211,238,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(34,211,238,0.3) 1px, transparent 1px)',
                backgroundSize: '40px 40px'
              }}
            />
            <div className="absolute w-48 h-48 rounded-full border border-cyan-900/30" />
            <div className="absolute w-32 h-32 rounded-full border border-cyan-900/40" />
            <div className="absolute w-16 h-16 rounded-full border border-cyan-900/60" />
            <div className="absolute w-2 h-2 bg-cyan-400 rounded-full shadow-[0_0_16px_rgba(34,211,238,0.9)]" />
            {[
              { top: '25%', left: '30%', color: 'bg-red-500' },
              { top: '40%', left: '65%', color: 'bg-amber-500' },
              { top: '55%', left: '45%', color: 'bg-emerald-500' },
              { top: '35%', left: '75%', color: 'bg-red-400' },
              { top: '60%', left: '20%', color: 'bg-amber-400' },
              { top: '20%', left: '55%', color: 'bg-cyan-400' },
              { top: '70%', left: '60%', color: 'bg-red-500' },
              { top: '45%', left: '15%', color: 'bg-emerald-400' },
            ].map((p, i) => (
              <div
                key={i}
                className={`absolute w-2 h-2 ${p.color} rounded-full opacity-80 animate-pulse`}
                style={{ top: p.top, left: p.left }}
              />
            ))}
            <span className="relative z-10 text-xs text-gray-600 font-mono">THREAT MAP — AWAITING DATA FEED</span>
          </div>
        </Panel>

        {/* Threat Severity Donut */}
        <Panel>
          <div className="flex items-center gap-2 mb-4">
            <Crosshair className="w-5 h-5 text-amber-400" />
            <h2 className="text-sm font-bold tracking-wider text-gray-200 uppercase">Threat Severity</h2>
          </div>
          <div className="flex flex-col items-center">
            <Suspense fallback={<div className="h-[250px] flex items-center justify-center text-xs font-mono text-gray-600">Loading chart...</div>}>
              <ErrorBoundary fallback="Severity chart unavailable">
                <SeverityDonut />
              </ErrorBoundary>
            </Suspense>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2 text-xs font-mono">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-red-500" />
              <span className="text-gray-400">Critical</span>
              <span className="ml-auto text-red-400">{riskDistribution.CRITICAL}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-amber-500" />
              <span className="text-gray-400">High</span>
              <span className="ml-auto text-amber-400">{riskDistribution.HIGH}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-yellow-400" />
              <span className="text-gray-400">Medium</span>
              <span className="ml-auto text-yellow-400">{riskDistribution.MEDIUM}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-400" />
              <span className="text-gray-400">Low</span>
              <span className="ml-auto text-emerald-400">{riskDistribution.LOW}</span>
            </div>
          </div>
        </Panel>
      </div>

      {/* ── Second Row: Network Activity + Quick Panels ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Network Activity */}
        <Panel className="lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-cyan-400" />
              <h2 className="text-sm font-bold tracking-wider text-gray-200 uppercase">Network Activity</h2>
            </div>
          </div>
          <Suspense fallback={<div className="h-[300px] flex items-center justify-center text-xs font-mono text-gray-600">Loading chart...</div>}>
            <ErrorBoundary fallback="Traffic chart unavailable">
              <TrafficChart />
            </ErrorBoundary>
          </Suspense>
        </Panel>

        {/* Quick Status Panel */}
        <div className="space-y-4">
          <Panel>
            <div className="flex items-center gap-2 mb-3">
              <Lock className="w-4 h-4 text-emerald-400" />
              <h3 className="text-xs font-bold tracking-wider text-gray-300 uppercase">Firewall Status</h3>
            </div>
            <div className="space-y-2 text-xs font-mono">
              <StatRow label="Rules Active" value="1,247" />
              <StatRow label="Blocked Today" value="834" />
              <StatRow label="Avg Latency" value="12ms" />
              <StatRow label="Uptime" value="99.97%" color="text-emerald-400" />
            </div>
          </Panel>

          <Panel>
            <div className="flex items-center gap-2 mb-3">
              <Eye className="w-4 h-4 text-amber-400" />
              <h3 className="text-xs font-bold tracking-wider text-gray-300 uppercase">IDS / IPS</h3>
            </div>
            <div className="space-y-2 text-xs font-mono">
              <StatRow label="Signatures" value="14,832" />
              <StatRow label="Alerts (24h)" value="23" color="text-amber-400" />
              <StatRow label="Intrusions Blocked" value="7" color="text-emerald-400" />
              <StatRow label="Last Update" value="2 min ago" />
            </div>
          </Panel>
        </div>
      </div>

      {/* ── Recent Alerts ── */}
      <Panel>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-amber-400" />
            <h2 className="text-sm font-bold tracking-wider text-gray-200 uppercase">Recent Alerts</h2>
          </div>
          <button className="text-xs text-cyan-400 hover:text-cyan-300 font-mono transition-colors cursor-pointer">
            View All →
          </button>
        </div>
        <div className="space-y-2">
          {recentAlerts.length === 0 ? (
            <div className="flex items-center justify-center py-8 text-gray-600 text-sm font-mono">
              No active alerts
            </div>
          ) : (
            recentAlerts.map((alert, idx) => (
              <div
                key={alert.id || idx}
                className="flex items-center justify-between px-4 py-3 bg-gray-950/50 rounded border border-gray-800/60 hover:border-gray-700 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className={`px-2 py-0.5 rounded text-xs font-mono font-bold uppercase border ${SEV_COLORS[getAlertSeverity(alert)]}`}>
                    {getAlertSeverity(alert)}
                  </span>
                  <div>
                    <p className="text-sm font-medium text-gray-200">{getAlertType(alert)}</p>
                    <p className="text-xs text-gray-500 font-mono">{getAlertMessage(alert)}</p>
                  </div>
                </div>
                <span className="text-xs text-gray-600 font-mono flex-shrink-0 ml-4">{getAlertTime(alert.detected_at)}</span>
              </div>
            ))
          )}
        </div>
      </Panel>

      {/* ── Bottom Row: Placeholder Panels ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Live Feed */}
        <Panel>
          <div className="flex items-center gap-2 mb-3">
            <Radio className="w-4 h-4 text-cyan-400" />
            <h3 className="text-xs font-bold tracking-wider text-gray-300 uppercase">Live Event Feed</h3>
          </div>
          <div className="space-y-2 max-h-48 overflow-y-auto font-mono text-xs">
            {[
              { ts: '14:32:07', msg: 'SSH brute force detected on 10.0.0.4', sev: 'text-red-400' },
              { ts: '14:31:55', msg: 'DNS query to known C2 domain blocked', sev: 'text-amber-400' },
              { ts: '14:31:42', msg: 'New asset discovered on subnet 10.0.1.x', sev: 'text-cyan-400' },
              { ts: '14:31:18', msg: 'SSL certificate expiring in 7 days — srv-003', sev: 'text-amber-400' },
              { ts: '14:30:59', msg: 'Firewall rule updated by admin@corp.io', sev: 'text-gray-400' },
              { ts: '14:30:44', msg: 'Geofence alert: login from Moscow, RU', sev: 'text-red-400' },
              { ts: '14:30:21', msg: 'Threat intel feed updated (+23 indicators)', sev: 'text-emerald-400' },
            ].map((e, i) => (
              <div key={i} className="flex gap-2">
                <span className="text-gray-600">{e.ts}</span>
                <span className={e.sev}>{e.msg}</span>
              </div>
            ))}
          </div>
        </Panel>

        {/* Asset Status */}
        <Panel>
          <div className="flex items-center gap-2 mb-3">
            <Cpu className="w-4 h-4 text-emerald-400" />
            <h3 className="text-xs font-bold tracking-wider text-gray-300 uppercase">Asset Inventory</h3>
          </div>
          <div className="space-y-2 text-xs font-mono">
            <StatRow label="Servers Online" value="24" color="text-emerald-400" />
            <StatRow label="Endpoints" value="186" />
            <StatRow label="Network Devices" value="42" />
            <StatRow label="Vulnerabilities Open" value="17" color="text-red-400" />
            <StatRow label="Compliance Rate" value="94.2%" color="text-emerald-400" />
          </div>
          <div className="mt-3 pt-3 border-t border-gray-800">
            <div className="flex items-center justify-between">
              <span className="text-gray-500">Patch Status</span>
              <div className="w-40 h-1.5 bg-gray-800 rounded-full overflow-hidden">
                <div className="h-full w-[94%] bg-emerald-400 rounded-full shadow-[0_0_8px_rgba(52,211,153,0.6)]" />
              </div>
            </div>
          </div>
        </Panel>

        {/* Terminal / Command Log */}
        <Panel>
          <div className="flex items-center gap-2 mb-3">
            <Terminal className="w-4 h-4 text-gray-400" />
            <h3 className="text-xs font-bold tracking-wider text-gray-300 uppercase">System Log</h3>
          </div>
          <div className="bg-gray-950 rounded border border-gray-800 p-3 font-mono text-xs space-y-1 max-h-48 overflow-y-auto">
            <p><span className="text-emerald-400">✓</span> <span className="text-gray-400">Pipeline ingester started</span></p>
            <p><span className="text-emerald-400">✓</span> <span className="text-gray-400">Student-1 auth service online</span></p>
            <p><span className="text-amber-400">⚠</span> <span className="text-gray-400">n8n webhook retry (attempt 2)</span></p>
            <p><span className="text-emerald-400">✓</span> <span className="text-gray-400">Playbook auto-response enabled</span></p>
            <p><span className="text-gray-600">·</span> <span className="text-gray-500">Awaiting events...</span></p>
          </div>
        </Panel>
      </div>

    </div>
  );
};

const SEV_COLORS = {
  high: 'bg-red-500/20 text-red-400 border-red-500/30',
  medium: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  low: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
};

const Panel = ({ children, className = '' }) => (
  <div className={`bg-gray-900/80 border border-gray-800 rounded-lg p-5 ${className}`}>
    {children}
  </div>
);

const KPICard = ({ label, value, unit = '', icon: Icon, color, glow, trend, trendDir }) => (
  <div className={`bg-gray-900/80 border border-gray-800 rounded-lg p-5 ${glow || ''}`}>
    <div className="flex items-start justify-between mb-3">
      <p className="text-xs font-bold tracking-wider text-gray-500 uppercase">{label}</p>
      <Icon className={`w-5 h-5 ${color}`} />
    </div>
    <div className="flex items-end gap-2">
      <span className={`text-3xl font-bold font-mono ${color}`}>{value}{unit}</span>
      {trend && (
        <span className={`text-xs font-mono pb-1 ${trendDir === 'up' ? 'text-red-400' : trendDir === 'down' ? 'text-emerald-400' : 'text-gray-500'
          }`}>
          {trend}
        </span>
      )}
    </div>
  </div>
);

const StatRow = ({ label, value, color = 'text-gray-300' }) => (
  <div className="flex items-center justify-between">
    <span className="text-gray-500">{label}</span>
    <span className={`font-bold ${color}`}>{value}</span>
  </div>
);

export default Dashboard;
